import { Editor, useEditorState } from "@tiptap/react";
import { KEY_LABEL } from "components/CommandPalette/constants";
import { Button } from "components/DesignSystem/Button/Button";
import ToolTip from "components/design/toolTip";
import { Bold } from "components/icons/Bold";
import { Italic } from "components/icons/Italic";
import { LinkIcon } from "components/icons/LinkIcon";
import { ListIcon } from "components/icons/ListIcon";
import { NumberedList } from "components/icons/NumberedList";
import { useDeviceInfo } from "hooks/useDeviceInfo";
import { Fragment, ReactNode } from "react";

type ChatComposerToolbarProps = {
  editor: Editor;
  onOpenLinkInput: () => void;
};

type ToolbarItem = {
  label: string;
  shortcut: string;
  icon: ReactNode;
  isActive: boolean;
  onClick: () => void;
};

const SHIFT_KEY_LABEL = "⇧";

const ToolbarToggle = ({
  label,
  shortcut,
  icon,
  isActive,
  onClick,
}: ToolbarItem) => (
  <ToolTip text={`${label} (${shortcut})`}>
    <Button
      type="button"
      customType="ghost_icon"
      size="small"
      aria-label={label}
      aria-pressed={isActive}
      onClick={onClick}
      className="aria-pressed:t-bg-neutral-10 aria-pressed:t-text-text-100"
    >
      {icon}
    </Button>
  </ToolTip>
);

const ToolbarDivider = () => (
  <div role="separator" className="t-mx-1 t-h-5 t-w-px t-bg-neutral-10" />
);

export const ChatComposerToolbar = ({
  editor,
  onOpenLinkInput,
}: ChatComposerToolbarProps) => {
  const { isMac } = useDeviceInfo();
  const modifierKey = isMac ? KEY_LABEL.MAC_MODIFIER : KEY_LABEL.MODIFIER;
  const modifierShiftKey = `${modifierKey}${SHIFT_KEY_LABEL}`;

  const { isBold, isItalic, isLink, isOrderedList, isBulletList } =
    useEditorState({
      editor,
      selector: ({ editor: current }) => ({
        isBold: current.isActive("bold"),
        isItalic: current.isActive("italic"),
        isLink: current.isActive("link"),
        isOrderedList: current.isActive("orderedList"),
        isBulletList: current.isActive("bulletList"),
      }),
    });

  const toolbarGroups: ToolbarItem[][] = [
    [
      {
        label: "Bold",
        shortcut: `${modifierKey}B`,
        icon: <Bold />,
        isActive: isBold,
        onClick: () => editor.chain().focus().toggleBold().run(),
      },
      {
        label: "Italic",
        shortcut: `${modifierKey}I`,
        icon: <Italic />,
        isActive: isItalic,
        onClick: () => editor.chain().focus().toggleItalic().run(),
      },
    ],
    [
      {
        label: "Link",
        shortcut: `${modifierShiftKey}U`,
        icon: <LinkIcon />,
        isActive: isLink,
        onClick: onOpenLinkInput,
      },
    ],
    [
      {
        label: "Numbered list",
        shortcut: `${modifierShiftKey}7`,
        icon: <NumberedList />,
        isActive: isOrderedList,
        onClick: () => editor.chain().focus().toggleOrderedList().run(),
      },
      {
        label: "Bulleted list",
        shortcut: `${modifierShiftKey}8`,
        icon: <ListIcon />,
        isActive: isBulletList,
        onClick: () => editor.chain().focus().toggleBulletList().run(),
      },
    ],
  ];

  return (
    <div
      role="toolbar"
      aria-label="Formatting"
      className="t-flex t-items-center t-justify-start t-gap-0.5"
    >
      {toolbarGroups.map((group, index) => (
        <Fragment key={group[0].label}>
          {index > 0 && <ToolbarDivider />}
          {group.map((item) => (
            <ToolbarToggle key={item.label} {...item} />
          ))}
        </Fragment>
      ))}
    </div>
  );
};
